import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UserLoginService } from './routes/user-login/user-login.service';
import { SystemVariablesService } from './modules/settings/super-admin/services/system-variables.service';
import { ContactTypesService } from './modules/settings/other/services/contact-types.service';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

/**
_______________________________________________________________________________
Root component of the Growing Tykes CMS application. Serves as the application shell
and handles initial bootstrapping logic including authentication checks and
preloading of global data.
_______________________________________________________________________________
*/
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'gtlc';

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private router: Router,
    private loginService: UserLoginService,
    private systemVariablesService: SystemVariablesService,
    private contactTypesService: ContactTypesService,
    private msalBroadcastService: MsalBroadcastService,
  ) {}

  /**
  _______________________________________________________________________________
  Initializes the component on startup. Checks authentication state and preloads
  global data including system variables and contact types. Redirects to the
  general dashboard after a 1-second delay if user is not logged in.
  _______________________________________________________________________________
  */
  ngOnInit() {
    const isLoggedIn = this.loginService.isLoggedIn();

    // Wait for MSAL to finish processing the redirect response (and set the
    // active account) before firing calls to protected endpoints - otherwise
    // MsalInterceptor's silent token acquisition finds no account yet and
    // falls back to its own interactive redirect, on top of MsalGuard's,
    // which is what caused the app to prompt for login twice.
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.systemVariablesService.getAllSystemVariables();
        this.contactTypesService.getAllContactTypes();
      });

    if (!isLoggedIn) {
      setTimeout(() => {
        this.router.navigate(['dashboard', 'general']);
      }, 1000);
    }
  }

  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
